// @flow
import React from 'react'
import { withRouter } from 'react-router-dom'
import Tree, { TreeNode } from 'er2-rc-tree'
import { parse, stringify } from 'query-string'
import { getFicusPathFromNode, isTransimTheme } from '../util/ficus'
import type { BookmarkType } from '../reducers/select_bookmark_bar'
import styles from '../styles'
import type { SettingsBarType } from '../reducers/settings'
import type { SelectFrameworkBarType } from '../reducers/select_framework_bar'

type Props = {
    bookmarksLocal: BookmarkType[],
    bookmarksGlobal: BookmarkType[],
    bookmarksMRU: BookmarkType[],
    fetchBookmarks: Function,
    ficus: SelectFrameworkBarType,
    ficusNode: string,
    history: Function,
    location: { pathname: string, hash: string, search: {} },
    moveBookmark: Function,
    removeBookmark: Function,
    setFicusData: Function,
    settings: SettingsBarType,
    theme: styles,
}

type State = {
    autoExpandParent: boolean,
    loadingFicusNode: string,
    filterEmpty: boolean,
    expandedKeys: string[],
    selectedKeys: string[],
    parentKeys: {},
}

/**
 * The frameworks tree
 */
class Bookmarks extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            searchText: '',
            autoExpandParent: true,
            filterEmpty: false,
            expandedKeys: ['bookmarksLocal', 'bookmarksGlobal', 'bookmarksMRU'],
            selectedKeys: [],
            parentKeys: {},
        }
    }

    render() {
        const onSelect = (nodeList, e) => {
            if (nodeList.length) {
                // Split collection type and node
                const spl = nodeList[0].indexOf(':')
                const ficusNode = nodeList[0].substr(spl + 1)
                if (ficusNode) {
                    this.props.setFicusData(ficusNode)
                    const params = { ...parse(this.props.location.search), theme: ficusNode }
                    this.props.history.push({ search: stringify(params) })
                    this.setState({ loadingFicusNode: ficusNode })
                }
            }
        }
        const onExpand = (expandedKeys) => {
            this.setState({ ...this.state, autoExpandParent: false, expandedKeys })
        }

        const onDragEnter = (info) => {
            this.setState({
                expandedKeys: info.expandedKeys,
            })
        }
        const onDrop = (info) => {
            const dropKey = info.node.props.eventKey
            const dragKey = info.dragNode.props.eventKey

            // Locate dragKey's collection
            let tokens = dragKey.split(':')
            const srcBookmarkType = tokens[0]
            const srcBookmark = tokens.length > 1 ? tokens[1] : ''

            // Locate the dropKey's collection.
            tokens = dropKey.split(':')
            const destBookmarkType = tokens[0]
            const destBookmark = tokens.length > 1 ? tokens[1] : ''
            this.props.moveBookmark(srcBookmarkType, srcBookmark, destBookmarkType, destBookmark)
        }

        const clientWidth = document.body ? document.body.clientWidth : 100
        const itemWidth = clientWidth > 300 ? clientWidth - 200 : clientWidth

        const titleStyle = {
            maxWidth: `${itemWidth}px`,
        }
        const bookmarkEntryStyle = {
            backgroundColor: `rgba(255,255,255,${this.props.settings.popupOpacity})`,
        }
        const buildTreeData = ((bookmarkType: string) => this.props[bookmarkType].map((bookmark: BookmarkType) => {
            const onDelete = (e) => {
                this.props.removeBookmark(bookmarkType, bookmark.ficusNode)
                e.stopPropagation()
            }

            const iconStyle = { marginLeft: '10px', verticalAlign: 'top', display: 'inline-block' }
            const getDeleteIcon = () => (
                <span style={iconStyle}>
                    <a onClick={onDelete} target="node" role="button" tabIndex={-1} style={{ color: 'black' }}>
                        <i className={'fa fa-trash'} />
                    </a>
                </span>
            )

            const classes = [this.props.theme.treeFrameworkTitle]
            const ficusNode = bookmark.ficusNode || bookmark
            if (bookmark.error) classes.push(this.props.theme.metaError)

            let loading
            if (this.props.ficus.fetchingFicusData && ficusNode === this.state.loadingFicusNode) {
                loading = (
                    <img
                        alt="loading frameworks"
                        style={{ marginLeft: '4px' }}
                        src="/static/er2_ficus/img/loading.gif"
                    />
                )
            }

            return (
                <TreeNode
                    key={`${bookmarkType}:${ficusNode}`}
                    className={this.props.theme.treeLeafEntry}
                    data={`${bookmarkType}:${ficusNode}`}
                    title={(
                        <span
                            className={this.props.theme.treeFrameworkTitleContainer}
                            style={bookmarkEntryStyle}
                        >
                            <span
                                className={classes.join(' ')}
                                style={titleStyle}
                            >
                                {getFicusPathFromNode(ficusNode) /* old-style bookmarks will only be a string */}
                            </span>
                            {loading}{getDeleteIcon()}
                        </span>
                    )}
                />
            )
        }))

        const bookmarkTypeText = {
            bookmarksLocal: 'Bookmarks',
            bookmarksGlobal: 'Shared Bookmarks',
            bookmarksMRU: 'History',
        }

        const exportBookmarks = () => {
            window.open('/er2_ficus/exportBookmarks/')
        }

        return (
            <div>
                <div className={this.props.theme.toolbuttonPanel}>
                    <button
                        className={this.props.theme.toolbutton}
                        onClick={() => this.props.fetchBookmarks()}
                        title="Update global framework bookmarks"
                    >
                        <i className={'fa fa-refresh'} />
                    </button>
                    <button
                        className={this.props.theme.toolbutton}
                        onClick={exportBookmarks}
                        title="Export shared bookmarks as text"
                    >
                        <i className={'fa fa-download'} />
                    </button>
                </div>

                <div style={{ marginTop: '10px' }}>
                    <div className={this.props.theme.container}>
                        <Tree
                            draggable
                            onDragEnter={e => onDragEnter(e)}
                            onDrop={e => onDrop(e)}
                            showIcon={false}
                            showLine={false}
                            onSelect={(...args) => onSelect(...args)}
                            onExpand={(...args) => onExpand(...args)}
                            expandedKeys={this.state.expandedKeys}
                            autoExpandParent={this.state.autoExpandParent}
                            selectedKeys={this.state.selectedKeys}
                        >
                            {['bookmarksMRU', 'bookmarksLocal', 'bookmarksGlobal'].map(bookmarkType => (
                                <TreeNode
                                    key={bookmarkType}
                                    className={this.props.theme.treeRootEntry}
                                    data={bookmarkType}
                                    title={(<span className={this.props.theme.ficusControlsHeaderTitle}>{bookmarkTypeText[bookmarkType]}</span>)}
                                >
                                    {buildTreeData(bookmarkType)}
                                </TreeNode>
                            ))}
                        </Tree>
                    </div>
                </div>
            </div>
        )
    }
}

export default withRouter(Bookmarks)
